import translate from '@translate';
import React, { FC } from 'react';
import Grid, { GridItem } from 'src/components/Grid';
import Select from 'src/components/Select';
import TextField from 'src/components/TextField';
import { OrderType } from 'src/types/order';
import getCountryOptions from 'src/utils/countryOptions';
import { BuildingIcon } from 'src/components/SkladonIcon/SmallIcons';
import { DeliveredIcon } from 'src/components/SkladonIcon/BigIcons';
import Typography from 'src/components/Typography';
import { IconTextStyle, StyledIcon } from './Recipient';
import Button from 'src/components/Button';
import { ProviderCompanyType } from 'src/types/providerCompany';
import { FormErrorsType, OnChangeType } from '../../types/global';
import { EditKeysType } from './constants';

export interface BillingPropsType {
    onChange: OnChangeType<EditKeysType>;
    editData: OrderType;
    editErrors: FormErrorsType;
    disableEdit: boolean;
    onSetProviderCompany: () => void;
    providerCompaniesData: null | ProviderCompanyType[];
    summary?: boolean;
}

const Billing: FC<BillingPropsType> = (props) => {
    return (
        <Grid>
            <GridItem cols={12} sx={{ marginBottom: '14px' }}>
                <Typography variant="h2" gutterBottom>
                    {translate('orderDetail.billingInfo')}
                    {!props.summary && !!props.providerCompaniesData?.length && !props.disableEdit && (
                        <Button
                            onClick={props.onSetProviderCompany}
                            className="order-select-provider-button"
                            mobileFullWidht
                        >
                            {translate('orderDetail.selectProviderBilling')}
                        </Button>
                    )}
                </Typography>
            </GridItem>
            <GridItem cols={6}>
                <Grid>
                    <GridItem cols={12} sx={{ marginBottom: '14px' }}>
                        <StyledIcon>
                            <BuildingIcon />
                        </StyledIcon>
                        <Typography variant="h3" gutterBottom sx={IconTextStyle}>
                            {translate('orderDetail.contact')}
                        </Typography>
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.BILLING_NAME}
                            error={props.editErrors.BILLING_NAME}
                            onChange={(value, error) => props.onChange('BILLING_NAME', value, error)}
                            name="BILLING_NAME"
                            label={translate('orderDetail.fullName')}
                            fullWidth
                            required
                            multiline
                            disabled={props.disableEdit || props.summary}
                            maxLength={35}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.BILLING_ATTENTION_NAME}
                            error={props.editErrors.BILLING_ATTENTION_NAME}
                            onChange={(value, error) => props.onChange('BILLING_ATTENTION_NAME', value, error)}
                            name="BILLING_ATTENTION_NAME"
                            label={translate('orderDetail.attentionName')}
                            fullWidth
                            required
                            multiline
                            disabled={props.disableEdit || props.summary}
                            maxLength={35}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.BILLING_COMPANY_NAME}
                            error={props.editErrors.BILLING_COMPANY_NAME}
                            onChange={(value, error) => props.onChange('BILLING_COMPANY_NAME', value, error)}
                            name="BILLING_COMPANY_NAME"
                            label={translate('orderDetail.companyName')}
                            fullWidth
                            multiline
                            disabled={props.disableEdit || props.summary}
                            maxLength={35}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.BILLING_GLN}
                            error={props.editErrors.BILLING_GLN}
                            onChange={(value, error) => props.onChange('BILLING_GLN', value, error)}
                            name="BILLING_GLN"
                            label={translate('orderDetail.GLN')}
                            fullWidth
                            disabled={props.disableEdit || props.summary}
                            maxLength={35}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.BILLING_EMAIL}
                            error={props.editErrors.BILLING_EMAIL}
                            onChange={(value, error) => props.onChange('BILLING_EMAIL', value, error)}
                            name="BILLING_EMAIL"
                            label={translate('orderDetail.email')}
                            fullWidth
                            required
                            multiline
                            disabled={props.disableEdit || props.summary}
                            type="email"
                            maxLength={50}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.BILLING_PHONE}
                            error={props.editErrors.BILLING_PHONE}
                            onChange={(value, error) => props.onChange('BILLING_PHONE', value, error)}
                            name="BILLING_PHONE"
                            label={translate('orderDetail.phone')}
                            fullWidth
                            required
                            multiline
                            disabled={props.disableEdit || props.summary}
                            maxLength={14}
                        />
                    </GridItem>
                </Grid>
            </GridItem>
            <GridItem cols={6}>
                <Grid>
                    <GridItem cols={12} sx={{ marginBottom: '14px' }}>
                        <StyledIcon>
                            <DeliveredIcon />
                        </StyledIcon>
                        <Typography variant="h3" gutterBottom sx={IconTextStyle}>
                            {translate('orderDetail.billingAddress')}
                        </Typography>
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.BILLING_STREET}
                            error={props.editErrors.BILLING_STREET}
                            onChange={(value, error) => props.onChange('BILLING_STREET', value, error)}
                            name="BILLING_STREET"
                            label={translate('orderDetail.billingStreet')}
                            fullWidth
                            required={!props.editData.ADDRESS_ID}
                            multiline
                            disabled={props.disableEdit || props.summary}
                            maxLength={35}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.BILLING_CITY}
                            error={props.editErrors.BILLING_CITY}
                            onChange={(value, error) => props.onChange('BILLING_CITY', value, error)}
                            name="BILLING_CITY"
                            label={translate('orderDetail.city')}
                            fullWidth
                            required={!props.editData.ADDRESS_ID}
                            multiline
                            disabled={props.disableEdit || props.summary}
                            maxLength={30}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.BILLING_POST_CODE}
                            error={props.editErrors.BILLING_POST_CODE}
                            onChange={(value, error) => props.onChange('BILLING_POST_CODE', value, error)}
                            name="BILLING_POST_CODE"
                            label={translate('orderDetail.postCode')}
                            fullWidth
                            required={!props.editData.ADDRESS_ID}
                            disabled={props.disableEdit || props.summary}
                            maxLength={9}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <Select
                            options={getCountryOptions()}
                            value={props.editData.BILLING_STATE}
                            error={props.editErrors.BILLING_STATE}
                            onChange={(value, error) => props.onChange('BILLING_STATE', value, error)}
                            name="BILLING_STATE"
                            label={translate('orderDetail.country')}
                            fullWidth
                            required={!props.editData.ADDRESS_ID}
                            disabled={props.disableEdit || props.summary}
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.BILLING_ADDRESS_ADDITIONAL_INFO}
                            error={props.editErrors.BILLING_ADDRESS_ADDITIONAL_INFO}
                            onChange={(value, error) => props.onChange('BILLING_ADDRESS_ADDITIONAL_INFO', value, error)}
                            name="BILLING_ADDRESS_ADDITIONAL_INFO"
                            label={translate('orderDetail.addressAdditionalInfo')}
                            fullWidth
                            disabled={props.disableEdit || props.summary}
                            maxLength={35}
                            multiline
                        />
                    </GridItem>
                    <GridItem cols={6}>
                        <TextField
                            value={props.editData.BILLING_PROVINCE_CODE}
                            error={props.editErrors.BILLING_PROVINCE_CODE}
                            onChange={(value, error) => props.onChange('BILLING_PROVINCE_CODE', value, error)}
                            name="BILLING_PROVINCE_CODE"
                            label={translate('orderDetail.provinceCode')}
                            fullWidth
                            disabled={props.disableEdit || props.summary}
                            maxLength={35}
                        />
                    </GridItem>
                </Grid>
            </GridItem>
        </Grid>
    );
};

export default Billing;
